import { useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { PeerType, TargetFilter } from "@tg/shared";
import { PEER_TYPES } from "@tg/shared";
import { api } from "../lib/api";
import { useAllGroups, useCacheStatus, useTags } from "../lib/hooks";
import { engine } from "../engine/MTProtoEngine";
import { useEngineStore } from "../store/useEngineStore";
import { Badge, Banner, Button, Card, Input, cn } from "../ui";

const typeLabel: Record<PeerType, string> = {
  group: "Grupo",
  supergroup: "Supergrupo",
  channel: "Canal",
  user: "Usuario",
  bot: "Bot",
};

function chunk<T>(arr: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
}

export function GroupsAndTags() {
  const qc = useQueryClient();
  const groupsQ = useAllGroups();
  const tagsQ = useTags();
  const cacheQ = useCacheStatus();
  const enumProgress = useEngineStore((s) => s.enumProgress);
  const connected = useEngineStore((s) => s.phase) === "connected";

  const [search, setSearch] = useState("");
  const [types, setTypes] = useState<Set<PeerType>>(new Set());
  const [tagFilter, setTagFilter] = useState<number | null>(null);
  const [activeOnly, setActiveOnly] = useState(true);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [newTag, setNewTag] = useState("");

  const groups = groupsQ.data ?? [];
  const tags = tagsQ.data ?? [];

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (activeOnly && !g.isActive) return false;
      if (types.size && !types.has(g.peerType)) return false;
      if (tagFilter != null && !g.tags.includes(tagFilter)) return false;
      if (s && !(g.title.toLowerCase().includes(s) || (g.username ?? "").toLowerCase().includes(s)))
        return false;
      return true;
    });
  }, [groups, search, types, tagFilter, activeOnly]);

  const currentFilter = (): TargetFilter => ({
    mode: tagFilter != null ? "tags" : search ? "name" : "all",
    q: search.trim() || undefined,
    tagIds: tagFilter != null ? [tagFilter] : undefined,
    tagMode: "any",
    includeTypes: types.size ? [...types] : undefined,
    activeOnly,
  });

  // refresh (enumeration)
  const [refreshing, setRefreshing] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  async function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    abortRef.current = new AbortController();
    try {
      await engine.enumerate(abortRef.current.signal);
      await qc.invalidateQueries({ queryKey: ["groups"] });
      await qc.invalidateQueries({ queryKey: ["cacheStatus"] });
    } catch {
      /* aborted or error */
    } finally {
      setRefreshing(false);
    }
  }

  const assign = useMutation({
    mutationFn: async ({ tagId, ids }: { tagId: number; ids: string[] }) => {
      for (const c of chunk(ids, 100)) await api.assignTag(tagId, c);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["groups"] }).then(() => qc.invalidateQueries({ queryKey: ["tags"] })),
  });
  const assignByFilter = useMutation({
    mutationFn: (tagId: number) => api.assignTagByFilter(tagId, currentFilter()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["groups"] }).then(() => qc.invalidateQueries({ queryKey: ["tags"] })),
  });
  const createTag = useMutation({
    mutationFn: (name: string) => api.createTag(name),
    onSuccess: () => {
      setNewTag("");
      qc.invalidateQueries({ queryKey: ["tags"] });
    },
  });
  const deleteTag = useMutation({
    mutationFn: (id: number) => api.deleteTag(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tags"] });
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
  });

  // virtualization
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirt = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 44,
    overscan: 12,
  });

  const allSelected = filtered.length > 0 && filtered.every((g) => selection.has(g.peerId));
  function toggleAll() {
    if (allSelected) setSelection(new Set());
    else setSelection(new Set(filtered.map((g) => g.peerId)));
  }
  function toggleOne(id: string) {
    setSelection((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Grupos & Tags</h1>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          {cacheQ.data && (
            <span className="flex items-center gap-1">
              <span className={cn("h-2 w-2 rounded-full", cacheQ.data.stale ? "bg-amber-500" : "bg-emerald-500")} />
              {cacheQ.data.lastFullRefreshAt
                ? `actualizado hace ${Math.round(cacheQ.data.ageHours ?? 0)}h`
                : "sin caché"}
            </span>
          )}
          <Button onClick={refresh} disabled={!connected || refreshing}>
            {refreshing ? `Refrescando… (${enumProgress?.seen ?? 0})` : "Refrescar grupos"}
          </Button>
        </div>
      </div>

      {!connected && <Banner kind="warn">Inicia sesión en Configuración para enumerar tus grupos.</Banner>}
      {refreshing && enumProgress && (
        <Banner kind="info">
          Enumerando… {enumProgress.seen} diálogos. Último: {enumProgress.currentTitle}
        </Banner>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
        {/* main */}
        <div className="space-y-3">
          <Card className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="max-w-xs"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre o @usuario…"
              />
              <label className="flex items-center gap-1 text-xs text-slate-400">
                <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                solo activos
              </label>
              <select
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs"
                value={tagFilter ?? ""}
                onChange={(e) => setTagFilter(e.target.value ? parseInt(e.target.value, 10) : null)}
              >
                <option value="">— filtrar por tag —</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.usageCount ?? 0})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-wrap gap-2">
              {PEER_TYPES.map((pt) => (
                <button
                  key={pt}
                  onClick={() =>
                    setTypes((prev) => {
                      const n = new Set(prev);
                      n.has(pt) ? n.delete(pt) : n.add(pt);
                      return n;
                    })
                  }
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs",
                    types.has(pt)
                      ? "border-sky-500 bg-sky-950 text-sky-200"
                      : "border-slate-700 text-slate-400",
                  )}
                >
                  {typeLabel[pt]}
                </button>
              ))}
              <span className="ml-auto self-center text-xs text-slate-500">
                {filtered.length.toLocaleString()} / {groups.length.toLocaleString()} · {selection.size} sel.
              </span>
            </div>
          </Card>

          {/* bulk actions */}
          {(selection.size > 0 || tagFilter != null || search) && (
            <Card className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-slate-400">Acciones:</span>
              <select
                id="bulkTag"
                className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs"
                defaultValue=""
              >
                <option value="">— elegir tag —</option>
                {tags.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <Button
                variant="secondary"
                disabled={selection.size === 0}
                onClick={() => {
                  const sel = (document.getElementById("bulkTag") as HTMLSelectElement)?.value;
                  if (sel) assign.mutate({ tagId: parseInt(sel, 10), ids: [...selection] });
                }}
              >
                Asignar a seleccionados ({selection.size})
              </Button>
              <Button
                disabled={assignByFilter.isPending}
                onClick={() => {
                  const sel = (document.getElementById("bulkTag") as HTMLSelectElement)?.value;
                  if (sel) assignByFilter.mutate(parseInt(sel, 10));
                }}
              >
                Etiquetar TODO lo que coincide ({filtered.length})
              </Button>
            </Card>
          )}

          {/* table */}
          <Card className="p-0">
            <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-2 text-xs text-slate-400">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              <span className="flex-1">Título</span>
              <span className="w-24">Tipo</span>
              <span className="w-16 text-center">Enviar</span>
            </div>
            <div ref={parentRef} className="h-[60vh] overflow-auto">
              <div style={{ height: rowVirt.getTotalSize(), position: "relative" }}>
                {rowVirt.getVirtualItems().map((vi) => {
                  const g = filtered[vi.index]!;
                  return (
                    <div
                      key={g.peerId}
                      className="absolute left-0 right-0 flex items-center gap-2 border-b border-slate-900 px-3 text-sm"
                      style={{ height: 44, transform: `translateY(${vi.start}px)` }}
                    >
                      <input
                        type="checkbox"
                        checked={selection.has(g.peerId)}
                        onChange={() => toggleOne(g.peerId)}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-slate-200">{g.title}</div>
                        <div className="truncate text-xs text-slate-500">
                          {g.username ? `@${g.username}` : g.peerId}
                          {g.tags.map((tid) => {
                            const t = tags.find((x) => x.id === tid);
                            return t ? (
                              <Badge key={tid} color={t.color} className="ml-1">
                                {t.name}
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      </div>
                      <span className="w-24 text-xs text-slate-400">{typeLabel[g.peerType]}</span>
                      <span className="w-16 text-center">
                        {g.canSend ? "✓" : <span title={g.canSendReason ?? ""}>✗</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* tag manager */}
        <Card className="space-y-3 self-start">
          <h2 className="text-sm font-medium text-slate-300">Etiquetas</h2>
          <div className="flex gap-2">
            <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder="Nueva etiqueta" />
            <Button onClick={() => newTag.trim() && createTag.mutate(newTag.trim())}>+</Button>
          </div>
          <div className="space-y-1">
            {tags.map((t) => (
              <div key={t.id} className="flex items-center gap-2 text-sm">
                <Badge color={t.color}>{t.name}</Badge>
                <span className="text-xs text-slate-500">{t.usageCount ?? 0}</span>
                <button
                  className="ml-auto text-xs text-red-400 hover:text-red-300"
                  onClick={() => deleteTag.mutate(t.id)}
                >
                  borrar
                </button>
              </div>
            ))}
            {tags.length === 0 && <p className="text-xs text-slate-500">Sin etiquetas todavía.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
